<template>
  <div class="page-container">
    <MainTitle title="Practice. Learn. Repeat. 💬" />
    <div class="app-content-container">
      <div class="history-wrapper">
        <!-- Header -->
        <div class="history-header">
          <Button
            text
            icon="pi pi-arrow-left"
            label="Back to Hub"
            class="back-btn"
            @click="goToHub"
          />
          <h1 class="history-title">
            Past Conversations
          </h1>
        </div>

        <!-- Loading state -->
        <div
          v-if="status === 'loading'"
          class="status-container"
        >
          <ProgressSpinner />
          <p class="status-text">
            {{ CHAT_CONVERSATIONS_LOADING_MSG }}
          </p>
        </div>

        <!-- Error state -->
        <div
          v-else-if="status === 'error'"
          class="status-container"
        >
          <i class="pi pi-exclamation-triangle status-icon error-icon" />
          <p class="status-text">
            {{ CHAT_CONVERSATIONS_ERROR_MSG }}
          </p>
          <Button
            label="Retry"
            icon="pi pi-refresh"
            @click="loadConversations"
          />
        </div>

        <!-- Empty state -->
        <div
          v-else-if="status === 'loaded' && conversations.length === 0"
          class="status-container"
        >
          <i class="pi pi-comments status-icon empty-icon" />
          <p class="status-text">
            {{ CHAT_CONVERSATIONS_EMPTY_STATE_MSG }}
          </p>
          <Button
            label="Start a Conversation"
            icon="pi pi-play"
            @click="startNewChat"
          />
        </div>

        <!-- Conversations list -->
        <div
          v-else-if="status === 'loaded'"
          class="conversations-list"
        >
          <ConversationCard
            v-for="conversation in conversations"
            :key="conversation.id"
            :conversation="conversation"
            @click="openConversation"
            @deleted="removeConversation"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import MainTitle from '@/components/title/MainTitle.vue'
import ConversationCard from './ConversationCard.vue'
import { fetchPastConversations } from '@/services/chatService'
import {
  CHAT_CONVERSATIONS_LOADING_MSG,
  CHAT_CONVERSATIONS_ERROR_MSG,
  CHAT_CONVERSATIONS_EMPTY_STATE_MSG,
  CHAT_CONVERSATIONS_MAX_ITEMS
} from '@/assets/constants/constants'

export default {
  name: 'ChatHistory',

  components: { Button, ProgressSpinner, MainTitle, ConversationCard },

  data() {
    return {
      status: 'loading',
      conversations: [],
      CHAT_CONVERSATIONS_LOADING_MSG,
      CHAT_CONVERSATIONS_ERROR_MSG,
      CHAT_CONVERSATIONS_EMPTY_STATE_MSG
    }
  },

  mounted() {
    this.loadConversations()
  },

  methods: {
    async loadConversations() {
      this.status = 'loading'
      try {
        const all = await fetchPastConversations()
        this.conversations = all.slice(0, CHAT_CONVERSATIONS_MAX_ITEMS)
        this.status = 'loaded'
      } catch (error) {
        const statusCode = error.response?.status
        if (statusCode === 401 || statusCode === 403) {
          this.$router.push({ name: 'LoginForm' })
          return
        }
        this.status = 'error'
      }
    },
    openConversation(conversation) {
      this.$router.push({ name: 'ChatSummary', params: { sessionId: conversation.session_id, subject: conversation.scenario_subject } })
    },
    removeConversation(conversationId) {
      this.conversations = this.conversations.filter(c => c.id !== conversationId)
    },
    goToHub() {
      this.$router.push({ name: 'ChatHub' })
    },
    startNewChat() {
      this.$router.push({ name: 'ChatSubjectInput' })
    }
  }
}
</script>

<style scoped>
.history-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 760px;
  padding: 24px 0 48px;
  margin: 0 auto;
}

.history-header {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 32px;
}

.back-btn {
  padding-left: 0 !important;
}

.history-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: #1e2557;
}

.status-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 0;
}

.status-text {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0;
}

.status-icon {
  font-size: 2.5rem;
}

.error-icon {
  color: #dc2626;
}

.empty-icon {
  color: #a5b4fc;
}

.conversations-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}
</style>
